import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, ListItemText, MenuItem, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import * as Yup from "yup";
import {
  ID_CREDIT_NOTE,
  ID_DEBIT_NOTE,
  ID_INVOICE,
} from "../constants/InvoicesTypes";
import {
  ID_ACCOUNTED,
  ID_REISSUED,
  ID_SENT,
  ID_TO_SEND,
  ID_UNACCOUNTED,
} from "../constants/InvoicesStatuses";
import {
  ID_FILTER_CORRELATION_ID,
  ID_FILTER_CORRELATION_NAME,
  ID_FILTER_CUSTOMER_FIRST_NAME,
  ID_FILTER_CUSTOMER_LAST_NAME,
  ID_FILTER_CUSTOMER_COMPANY_NAME,
  ID_FILTER_CUSTOMER_ID,
  ID_FILTER_INVOICE_NUMBER,
  ID_FILTER_INVOICEDATEFROM,
  ID_FILTER_INVOICEDATETO,
  ID_FILTER_INVOICETYPE,
  ID_FILTER_PARENT_SAP_INVOICE_NUMBER,
  ID_FILTER_SAP_INVOICE_NUMBER,
  ID_FILTER_SEARCH,
  ID_FILTER_STATUS,
  invoiceDefaultValues,
  ID_FILTER_DUE_DATE_FROM,
  ID_FILTER_DUE_DATE_TO,
  ID_FILTER_CREATION_DATE_FROM,
  ID_FILTER_CREATION_DATE_TO,
  ID_FILTER_PAYMENT_METHOD,
  ID_FILTER_AMOUNT_TO,
  ID_FILTER_AMOUNT_FROM,
  ID_FILTER_TERMS_OF_PAYMENT,
  ID_FILTER_LEGAL_ENTITIES,
  ID_FILTER_DEALERS
} from "../constants/InvoicesFilters";
import {
  addFilter,
  DECIMAL_REG_EXP,
  getFilterValue,
  renderMultiSelect,
} from "../../../utils/FilterUtils";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { STATE_ACTIVE_FILTERS } from "../constants/InvoicesRecoil";
import {
  COLOR_ACCOUNTED,
  COLOR_REISSUED,
  COLOR_SENT,
  COLOR_TO_SEND,
} from "../constants/InvoicesColors";
import ColouredDot from "../../../component/ColouredDot";
import AdvancedFilter from "../../../component/AdvancedFilter";
import { asEndOfDay, asStartOfDay } from "../../../utils/FinanceDateUtils";
import { useLocation, useNavigate } from "react-router-dom";
import { ISelectValues } from "../../../component/ISelectValues";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";

function InvoicesFilter() {
  const { t } = useTranslation("finance", { keyPrefix: "invoices" });
  const { t: tg } = useTranslation("translation");

  // Use Context
  const {
    globalData: { user },
  } = useContext(GlobalContext);

  const legalEntityOptions: any[] = user.legalEntities
    ?.filter((x: any) => x.name.toLowerCase() !== "all")
    .map((x: any) => ({
      value: x.name,
      label: tg(`legalEntities.${x.name}`),
    }));

  const dealerOptions: any[] = user.dealers
    ?.filter((x: any) => x.name.toLowerCase() !== "all")
    .map((x: any) => ({
      value: x.name,
      label: tg(`dealers.${x.name}`),
    }));

  const setFilters = useSetRecoilState(STATE_ACTIVE_FILTERS);
  const filterDefs = useRecoilValue(STATE_ACTIVE_FILTERS);
  const [search, setSearch] = useState("");
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const customerId = queryParams.get("customerId");
  const navigate = useNavigate();
  const [dealers, setDealers] = useState<any[]>(dealerOptions);
  const schema = Yup.object()
    .shape({
      customerId: Yup.string().max(128, t("filters.errors.invalid-length")),
      customerFirstName: Yup.string().max(
        128,
        t("filters.errors.invalid-length")
      ),
      customerLastName: Yup.string().max(
        128,
        t("filters.errors.invalid-length")
      ),
      customerCompanyName: Yup.string().max(
        128,
        t("filters.errors.invalid-length")
      ),
      invoiceType: Yup.array(),
      statuses: Yup.array(),
      invoiceDateFrom: Yup.date()
        .nullable()
        .test(
          "testFrom",
          t("filters.errors.invalid-date"),
          (value, context) => {
            return (
              !value ||
              !context.parent.invoiceDateTo ||
              value <= context.parent.invoiceDateTo
            );
          }
        )
        .typeError(t("filters.errors.invalid-date")),
      invoiceDateTo: Yup.date()
        .nullable()
        .test("testTo", t("filters.errors.invalid-date"), (value, context) => {
          return (
            !value ||
            !context.parent.invoiceDateFrom ||
            value >= context.parent.invoiceDateFrom
          );
        })
        .typeError(t("filters.errors.invalid-date")),
      correlationName: Yup.string().max(
        128,
        t("filters.errors.invalid-length")
      ),
      correlationId: Yup.string().max(128, t("filters.errors.invalid-length")),
      dueDateFrom: Yup.date()
        .nullable()
        .test(
          "dueDateFrom",
          t("filters.errors.invalid-date"),
          (value, context) => {
            return (
              !value ||
              !context.parent.dueDateTo ||
              value <= context.parent.dueDateTo
            );
          }
        )
        .typeError(t("filters.errors.invalid-date")),
      dueDateTo: Yup.date()
        .nullable()
        .test(
          "dueDateTo",
          t("filters.errors.invalid-date"),
          (value, context) => {
            return (
              !value ||
              !context.parent.dueDateFrom ||
              value >= context.parent.dueDateFrom
            );
          }
        )
        .typeError(t("filters.errors.invalid-date")),
      creationDateFrom: Yup.date()
        .nullable()
        .test(
          "creationDateFrom",
          t("filters.errors.invalid-date"),
          (value, context) => {
            return (
              !value ||
              !context.parent.creationDateTo ||
              value <= context.parent.creationDateTo
            );
          }
        )
        .typeError(t("filters.errors.invalid-date")),
      creationDateTo: Yup.date()
        .nullable()
        .test(
          "creationDateTo",
          t("filters.errors.invalid-date"),
          (value, context) => {
            return (
              !value ||
              !context.parent.creationDateFrom ||
              value >= context.parent.creationDateFrom
            );
          }
        )
        .typeError(t("filters.errors.invalid-date")),
      amountFrom: Yup.string()
        .matches(DECIMAL_REG_EXP, {
          excludeEmptyString: true,
          message: t("filters.errors.decimal-numbers"),
        })
        .test(
          "testAmountFrom",
          t("filters.errors.invalid-number"),
          (value, context) => {
            return (
              !value ||
              !context.parent.amountTo ||
              value <= context.parent.amountTo
            );
          }
        ),
      amountTo: Yup.string()
        .matches(DECIMAL_REG_EXP, {
          excludeEmptyString: true,
          message: t("filters.errors.decimal-numbers"),
        })
        .test(
          "testAmountTo",
          t("filters.errors.invalid-number"),
          (value, context) => {
            return (
              !value ||
              !context.parent.amountFrom ||
              value >= context.parent.amountFrom
            );
          }
        ),
      dealers: Yup.array(),
      legalEntities: Yup.array().min(1, tg('validation.selectAtLeastOne'))
    })

  const defaultValues = {
    operator: "",
    customerId:
      getFilterValue(ID_FILTER_CUSTOMER_ID, filterDefs) || customerId || "",
    customerFirstName:
      getFilterValue(ID_FILTER_CUSTOMER_FIRST_NAME, filterDefs) || "",
    customerLastName:
      getFilterValue(ID_FILTER_CUSTOMER_LAST_NAME, filterDefs) || "",
    customerCompanyName:
      getFilterValue(ID_FILTER_CUSTOMER_COMPANY_NAME, filterDefs) || "",
    invoiceType: getFilterValue(ID_FILTER_INVOICETYPE, filterDefs) || [],
    statuses: getFilterValue(ID_FILTER_STATUS, filterDefs) || [],
    invoiceDateFrom:
      getFilterValue(ID_FILTER_INVOICEDATEFROM, filterDefs) || null,
    invoiceDateTo: getFilterValue(ID_FILTER_INVOICEDATETO, filterDefs) || null,
    correlationName:
      getFilterValue(ID_FILTER_CORRELATION_NAME, filterDefs) || "",
    correlationId: getFilterValue(ID_FILTER_CORRELATION_ID, filterDefs) || "",
    sapInvoiceNumber:
      getFilterValue(ID_FILTER_SAP_INVOICE_NUMBER, filterDefs) || "",
    parentSapInvoiceNumber:
      getFilterValue(ID_FILTER_PARENT_SAP_INVOICE_NUMBER, filterDefs) || "",
    invoiceNumber: getFilterValue(ID_FILTER_INVOICE_NUMBER, filterDefs) || "",

    dueDateFrom: getFilterValue(ID_FILTER_DUE_DATE_FROM, filterDefs) || null,
    dueDateTo: getFilterValue(ID_FILTER_DUE_DATE_TO, filterDefs) || null,
    creationDateFrom:
      getFilterValue(ID_FILTER_CREATION_DATE_FROM, filterDefs) || null,
    creationDateTo:
      getFilterValue(ID_FILTER_CREATION_DATE_TO, filterDefs) || null,
    paymentMethod: getFilterValue(ID_FILTER_PAYMENT_METHOD, filterDefs) || [],
    termsOfPayment:
      getFilterValue(ID_FILTER_TERMS_OF_PAYMENT, filterDefs) || "",
    amountFrom: getFilterValue(ID_FILTER_AMOUNT_FROM, filterDefs) || "",
    amountTo: getFilterValue(ID_FILTER_AMOUNT_TO, filterDefs) || "",
    legalEntities:
      getFilterValue(ID_FILTER_LEGAL_ENTITIES, filterDefs) ||
      legalEntityOptions.map((x: any) => x.value),
    dealers: getFilterValue(ID_FILTER_DEALERS, filterDefs) || [],
  };

  const renderCustomerFirstName = (field: any) => (
    <TextField
      fullWidth
      name="customerFirstName"
      label={t("filters.invoices." + ID_FILTER_CUSTOMER_FIRST_NAME)}
      value={field.value}
      onChange={field.onChange}
    ></TextField>
  );

  const renderCustomerLastName = (field: any) => (
    <TextField
      fullWidth
      name="customerLastName"
      label={t("filters.invoices." + ID_FILTER_CUSTOMER_LAST_NAME)}
      value={field.value}
      onChange={field.onChange}
    ></TextField>
  );

  const renderCustomerId = (field: any) => (
    <TextField
      fullWidth
      name="customerId"
      label={t("filters.invoices." + ID_FILTER_CUSTOMER_ID)}
      value={field.value}
      onChange={field.onChange}
    ></TextField>
  );

  const renderCustomerCompanyName = (field: any) => (
    <TextField
      fullWidth
      name="customerCompanyName"
      label={t("filters.invoices." + ID_FILTER_CUSTOMER_COMPANY_NAME)}
      value={field.value}
      onChange={field.onChange}
    ></TextField>
  );

  const renderInvoiceDateFrom = (
    field: any,
    error: any,
    state: any,
    values: any
  ) => (
    <DatePicker
      label={t("filters.invoices." + ID_FILTER_INVOICEDATEFROM)}
      views={["day"]}
      value={field.value}
      maxDate={
        values["invoiceDateTo"] !== null ? values["invoiceDateTo"] : null
      }
      onChange={field.onChange}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          id={"invoiceDateFrom"}
          error={error !== undefined}
          helperText={error ? error.message : ""}
        />
      )}
      inputFormat={"L"}
    />
  );

  const renderInvoiceDateTo = (
    field: any,
    error: any,
    state: any,
    values: any
  ) => (
    <DatePicker
      label={t("filters.invoices." + ID_FILTER_INVOICEDATETO)}
      views={["day"]}
      value={field.value}
      minDate={
        values["invoiceDateFrom"] !== null ? values["invoiceDateFrom"] : null
      }
      onChange={field.onChange}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          id={"invoiceDateTo"}
          error={error !== undefined}
          helperText={error ? error.message : ""}
        />
      )}
      inputFormat={"L"}
    />
  );

  const renderInvoiceType = (field: any) => (
    <TextField
      fullWidth
      select
      SelectProps={{
        multiple: true,
        renderValue: (selected: any) =>
          selected
            .map((s: any) => invoiceTypes.find((ss) => ss.value === s)!.label)
            .join(", "),
      }}
      label={t("filters.invoices." + ID_FILTER_INVOICETYPE)}
      value={field.value}
      onChange={field.onChange}
    >
      {invoiceTypes.map((status) => (
        <MenuItem key={status.value} value={status.value}>
          <Checkbox checked={field.value.indexOf(status.value) > -1} />
          <ListItemText primary={status.label} />
        </MenuItem>
      ))}
    </TextField>
  );

  const renderStatuses = (field: any) => (
    <TextField
      fullWidth
      select
      SelectProps={{
        multiple: true,
        renderValue: (selected: any) =>
          selected
            .map((s: any) => statuses.find((ss) => ss.value === s)!.label)
            .join(", "),
      }}
      label={t("filters.invoices." + ID_FILTER_STATUS)}
      value={field.value}
      onChange={field.onChange}
    >
      {statuses.map((status) => (
        <MenuItem key={status.value} value={status.value}>
          <Checkbox checked={field.value.indexOf(status.value) > -1} />
          <ColouredDot color={status?.color} />
          <ListItemText primary={status.label} />
        </MenuItem>
      ))}
    </TextField>
  );

  const renderInvoiceNumber = (field: any) => (
    <TextField
      fullWidth
      name="invoiceNumber"
      label={t("filters.invoices." + ID_FILTER_INVOICE_NUMBER)}
      value={field.value}
      onChange={field.onChange}
    ></TextField>
  );
  const renderSapInvoiceNumber = (field: any) => (
    <TextField
      fullWidth
      name="sapInvoiceNumber"
      label={t("filters.invoices." + ID_FILTER_SAP_INVOICE_NUMBER)}
      value={field.value}
      onChange={field.onChange}
    ></TextField>
  );
  const renderParentSapInvoiceNumber = (field: any) => (
    <TextField
      fullWidth
      name="parentSapInvoiceNumber"
      label={t("filters.invoices." + ID_FILTER_PARENT_SAP_INVOICE_NUMBER)}
      value={field.value}
      onChange={field.onChange}
    ></TextField>
  );

  const renderCorrelationName = (field: any) => (
    <TextField
      fullWidth
      select
      SelectProps={{
        multiple: false,
      }}
      label={t("filters.invoices." + ID_FILTER_CORRELATION_NAME)}
      value={field.value}
      onChange={field.onChange}
    >
      <MenuItem value={""}>
        <em />
      </MenuItem>
      {correlationName.map((c) => (
        <MenuItem key={c.value} value={c.value}>
          {c.label}
        </MenuItem>
      ))}
    </TextField>
  );
  const renderCorrelationId = (field: any) => (
    <TextField
      fullWidth
      name="correlationId"
      label={t("filters.invoices." + ID_FILTER_CORRELATION_ID)}
      value={field.value}
      onChange={field.onChange}
    ></TextField>
  );

  const renderDueDateFrom = (
    field: any,
    error: any,
    state: any,
    values: any
  ) => (
    <DatePicker
      label={t("filters.invoices." + ID_FILTER_DUE_DATE_FROM)}
      views={["day"]}
      value={field.value}
      maxDate={values["dueDateTo"] !== null ? values["dueDateTo"] : null}
      onChange={field.onChange}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          id={"dueDateFrom"}
          error={error !== undefined}
          helperText={error ? error.message : ""}
        />
      )}
      inputFormat={"L"}
    />
  );

  const renderDueDateTo = (field: any, error: any, state: any, values: any) => (
    <DatePicker
      label={t("filters.invoices." + ID_FILTER_DUE_DATE_TO)}
      views={["day"]}
      value={field.value}
      minDate={values["dueDateFrom"] !== null ? values["dueDateFrom"] : null}
      onChange={field.onChange}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          id={"dueDateTo"}
          error={error !== undefined}
          helperText={error ? error.message : ""}
        />
      )}
      inputFormat={"L"}
    />
  );

  const renderCreationDateFrom = (
    field: any,
    error: any,
    state: any,
    values: any
  ) => (
    <DatePicker
      label={t("filters.invoices." + ID_FILTER_CREATION_DATE_FROM)}
      views={["day"]}
      value={field.value}
      maxDate={
        values["creationDateTo"] !== null ? values["creationDateTo"] : null
      }
      onChange={field.onChange}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          id={"creationDateFrom"}
          error={error !== undefined}
          helperText={error ? error.message : ""}
        />
      )}
      inputFormat={"L"}
    />
  );

  const renderCreationDateTo = (
    field: any,
    error: any,
    state: any,
    values: any
  ) => (
    <DatePicker
      label={t("filters.invoices." + ID_FILTER_CREATION_DATE_TO)}
      views={["day"]}
      value={field.value}
      minDate={
        values["creationDateFrom"] !== null ? values["creationDateFrom"] : null
      }
      onChange={field.onChange}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          id={"creationDateTo"}
          error={error !== undefined}
          helperText={error ? error.message : ""}
        />
      )}
      inputFormat={"L"}
    />
  );

  const renderPaymentMethod = (field: any) => (
    <TextField
      fullWidth
      select
      SelectProps={{
        multiple: true,
        renderValue: (selected: any) =>
          selected
            .map((s: any) => paymentMethods.find((ss) => ss.value === s)!.label)
            .join(", "),
      }}
      label={t("filters.invoices." + ID_FILTER_PAYMENT_METHOD)}
      value={field.value}
      onChange={field.onChange}
    >
      {paymentMethods.map((status) => (
        <MenuItem key={status.value} value={status.value}>
          <Checkbox checked={field.value.indexOf(status.value) > -1} />
          <ListItemText primary={status.label} />
        </MenuItem>
      ))}
    </TextField>
  );

  const renderTermsOfPayment = (field: any) => (
    <TextField
      fullWidth
      name="termsOfPayment"
      label={t("filters.invoices." + ID_FILTER_TERMS_OF_PAYMENT)}
      value={field.value}
      onChange={field.onChange}
    ></TextField>
  );

  const renderAmountFrom = (
    field: any,
    error: any,
    formState: any,
    values: any
  ) => (
    <TextField
      fullWidth
      name={"amountFrom"}
      label={t("filters.invoices." + ID_FILTER_AMOUNT_FROM)}
      value={field.value}
      onChange={field.onChange}
      error={error !== undefined}
      helperText={error ? error.message : ""}
    />
  );
  const renderAmountTo = (
    field: any,
    error: any,
    formState: any,
    values: any
  ) => (
    <TextField
      fullWidth
      name={"amountFrom"}
      label={t("filters.invoices." + ID_FILTER_AMOUNT_TO)}
      value={field.value}
      onChange={field.onChange}
      error={error !== undefined}
      helperText={error ? error.message : ""}
    />
  );

  

  const fields = {
    customerId: renderCustomerId,
    customerFirstName: renderCustomerFirstName,
    customerLastName: renderCustomerLastName,
    customerCompanyName: renderCustomerCompanyName,
    invoiceType: renderInvoiceType,
    invoiceNumber: renderInvoiceNumber,
    sapInvoiceNumber: renderSapInvoiceNumber,
    parentSapInvoiceNumber: renderParentSapInvoiceNumber,
    invoiceDateFrom: renderInvoiceDateFrom,
    invoiceDateTo: renderInvoiceDateTo,
    correlationName: renderCorrelationName,
    correlationId: renderCorrelationId,
    statuses: renderStatuses,
    amountFrom: renderAmountFrom,
    amountTo: renderAmountTo,
    paymentMethod: renderPaymentMethod,
    dueDateFrom: renderDueDateFrom,
    dueDateTo: renderDueDateTo,
    creationDateFrom: renderCreationDateFrom,
    creationDateTo: renderCreationDateTo,
    termsOfPayment: renderTermsOfPayment,
    legalEntities: renderMultiSelect(
      tg("shared.legalEntities"),
      legalEntityOptions,
      "legalEntities",
      legalEntityOptions.length <= 1,
      (value: any, setValue: any) => {
        setValue('dealers', []);
        let dealers = user?.dealers
          ?.filter(
            (x: any) =>
              value?.length > 0 &&
              x.legalEntities.some((e: any) => value.includes(e.name)) &&
              x.name.toLowerCase() !== "all"
          )
          .map((x: any) => {
            return {
              label: tg(`dealers.${x.name}`),
              value: x.name,
            };
          });
        setDealers(dealers);
      }
    ),
    dealers: renderMultiSelect(tg("shared.dealers"), dealers, "dealers", dealerOptions.length <= 1),
  };

  const invoiceTypes = [
    {
      value: ID_INVOICE,
      label: t("types." + ID_INVOICE),
    },
    {
      value: ID_CREDIT_NOTE,
      label: t("types." + ID_CREDIT_NOTE),
    },
    {
      value: ID_DEBIT_NOTE,
      label: t("types." + ID_DEBIT_NOTE),
    },
  ];

  const statuses = [
    {
      value: ID_TO_SEND,
      label: t("statuses." + ID_TO_SEND),
      color: COLOR_TO_SEND + ".transparent",
    },
    {
      value: ID_SENT,
      label: t("statuses." + ID_SENT),
      color: COLOR_SENT + ".transparent",
    },
    {
      value: ID_ACCOUNTED,
      label: t("statuses." + ID_ACCOUNTED),
      color: COLOR_ACCOUNTED + ".transparent",
    },
    {
      value: ID_UNACCOUNTED,
      label: t("statuses." + ID_UNACCOUNTED),
      color: "neutral.light",
    },
    {
      value: ID_REISSUED,
      label: t("statuses." + ID_REISSUED),
      color: COLOR_REISSUED + ".transparent",
    },
  ];

  const paymentMethods = [
    {
      value: "J",
      label: t("paymentMethods.CREDITCARD"),
    },
    {
      value: "D",
      label: t("paymentMethods.DEBITCARD"),
    },
    {
      value: "B",
      label: t("paymentMethods.BANKTRANSFER"),
    },
    {
      value: "P",
      label: t("paymentMethods.AMAZON"),
    },
    {
      value: "A",
      label: t("paymentMethods.AMEXSOF"),
    },
  ];

  const correlationName: ISelectValues[] = [
    {
      value: "SUBSCRIPTION",
      label: t("types.SUBSCRIPTION"),
    },
    {
      value: "MOBILITY VOUCHER",
      label: t("types.MOBILITY_VOUCHER"),
    },
    {
      value: "AMAZON VOUCHER",
      label: t("types.AMAZON_VOUCHER"),
    },
    {
      value: "PRE_PAID",
      label: t("types.PRE_PAID"),
    },
    {
      value: "RA",
      label: t("types.RA"),
    },
    {
      value: "DRIVALIA_RECHARGE",
      label: t("types.DRIVALIA_RECHARGE"),
    },
  ];

  const submit = (data: any) => {
    addFilter(setFilters, ID_FILTER_SEARCH, search);
    addFilter(
      setFilters,
      ID_FILTER_CUSTOMER_FIRST_NAME,
      data.customerFirstName
    );
    addFilter(setFilters, ID_FILTER_CUSTOMER_LAST_NAME, data.customerLastName);
    addFilter(setFilters, ID_FILTER_CUSTOMER_ID, data.customerId);
    addFilter(
      setFilters,
      ID_FILTER_CUSTOMER_COMPANY_NAME,
      data.customerCompanyName
    );
    addFilter(setFilters, ID_FILTER_INVOICETYPE, data.invoiceType);
    addFilter(
      setFilters,
      ID_FILTER_INVOICEDATEFROM,
      asStartOfDay(data.invoiceDateFrom)
    );
    addFilter(
      setFilters,
      ID_FILTER_INVOICEDATETO,
      asEndOfDay(data.invoiceDateTo)
    );
    addFilter(setFilters, ID_FILTER_STATUS, data.statuses);
    addFilter(setFilters, ID_FILTER_SAP_INVOICE_NUMBER, data.sapInvoiceNumber);
    addFilter(
      setFilters,
      ID_FILTER_PARENT_SAP_INVOICE_NUMBER,
      data.parentSapInvoiceNumber
    );
    addFilter(setFilters, ID_FILTER_INVOICE_NUMBER, data.invoiceNumber);
    addFilter(setFilters, ID_FILTER_CORRELATION_NAME, data.correlationName);
    addFilter(setFilters, ID_FILTER_CORRELATION_ID, data.correlationId);
    addFilter(
      setFilters,
      ID_FILTER_DUE_DATE_FROM,
      asStartOfDay(data.dueDateFrom)
    );
    addFilter(setFilters, ID_FILTER_DUE_DATE_TO, asEndOfDay(data.dueDateTo));
    addFilter(
      setFilters,
      ID_FILTER_CREATION_DATE_FROM,
      asStartOfDay(data.creationDateFrom)
    );
    addFilter(
      setFilters,
      ID_FILTER_CREATION_DATE_TO,
      asEndOfDay(data.creationDateTo)
    );
    addFilter(setFilters, ID_FILTER_PAYMENT_METHOD, data.paymentMethod);
    addFilter(setFilters, ID_FILTER_TERMS_OF_PAYMENT, data.termsOfPayment);
    addFilter(setFilters, ID_FILTER_AMOUNT_FROM, data.amountFrom);
    addFilter(setFilters, ID_FILTER_AMOUNT_TO, data.amountTo);
    addFilter(setFilters, ID_FILTER_LEGAL_ENTITIES, data.legalEntities);
    addFilter(setFilters, ID_FILTER_DEALERS, data.dealers);
  };

  const reset = () => {
    setFilters([]);
    setDealers(dealerOptions);
    if (customerId) {
      queryParams.delete("customerId");
      const newUrl = `${location.pathname}`;
      navigate(newUrl);
    }
  };

  return (
    <AdvancedFilter
      schema={schema}
      fields={fields}
      defaultValues={defaultValues}
      onSubmit={submit}
      onReset={reset}
      searchInput={search}
      setSearchInput={setSearch}
      customDefaultValues={invoiceDefaultValues}
    />
  );
}

export default InvoicesFilter;
